import React from 'react';
import Alert from 'react-bootstrap/Alert';

import Button from 'components/DesignSystem/Button';
import Icon from 'components/DesignSystem/Icon';
import TooltipWrapper from 'components/DesignSystem/TooltipWrapper';
import { AcaoSugestao, EstimativaCusto } from 'helper/windx';
import './index.scss';

interface MedidorComplexidadeProps {
  estimativa: EstimativaCusto | undefined;
  isFetching: boolean;
  // Aplica a agregação sugerida trocando a subdivisão espacial (escape verde).
  onAgregar: (subdivisaoEspacial: number) => void;
}

// Ação de agregação -> subdivisão espacial alvo (1=subsistemas, 3=conjuntos).
const SUBDIVISAO_POR_ACAO: Partial<Record<AcaoSugestao, number>> = {
  agregar_por_subsistema: 1,
  agregar_por_conjunto: 3,
};

const LABEL_ACAO: Record<AcaoSugestao, string> = {
  agregar_por_subsistema: 'Agregar por subsistema',
  agregar_por_conjunto: 'Agregar por conjunto',
  remover_simulacoes_antigas: 'Remover simulações antigas',
  usar_uma_rmv: 'Usar uma única RMV',
};

const VARIANT_POR_VEREDITO = {
  verde: 'success',
  amarelo: 'warning',
  vermelho: 'danger',
} as const;

const TOOLTIP_INVIAVEL = (
  'Acima do limite técnico que o gráfico consegue renderizar com fluidez. '
  + 'Reduza a seleção (entidades/modelos/RMV/simulações) ou agregue para uma '
  + 'subdivisão menor.'
);

/**
 * Medidor de complexidade (WINDX-051). Mostra o veredito do pré-voo + número
 * de séries (a métrica dominante) e esconde o detalhamento por fator num
 * tooltip ⓘ — mantém o painel estreito sem perder o diagnóstico.
 *
 * Vereditos: 🟢 verde segue; 🟡 amarelo avisa (legibilidade); 🔴 vermelho
 * bloqueia "Realizar Consulta" (gate no ControlPanel) e oferece um atalho
 * de agregação que volta ao verde. Renderiza `null` sem estimativa (seleção
 * incompleta ou tela ≠ WEOL-SH).
 */
export default function MedidorComplexidade({
  estimativa,
  isFetching,
  onAgregar,
}: MedidorComplexidadeProps) {
  if (!estimativa) return null;

  const {
    fatores,
    n_series: nSeries,
    payload_estimado_mb: payloadMb,
    veredito,
    sugestoes,
  } = estimativa;

  // Tooltip do ⓘ ao lado do nº de séries — detalhamento por fator + payload.
  const detalheFatores = (
    `${fatores.n_entidades.toLocaleString('pt-BR')} entidades × `
    + `${fatores.n_modelos} modelo(s) × `
    + `${fatores.n_rmv} RMV × `
    + `${fatores.n_simulacoes} simulação(ões)`
    + `${fatores.ren_por_usina ? ' + REN' : ''} `
    + `= ${nSeries.toLocaleString('pt-BR')} séries `
    + `(~${payloadMb} MB)`
  );

  // No vermelho, prioriza a 1ª sugestão de AGREGAÇÃO que resulta em verde
  // (subsistema costuma ser o escape mais agressivo); cai na 1ª de agregação.
  const escape = sugestoes.find(
    (s) => SUBDIVISAO_POR_ACAO[s.acao] !== undefined && s.veredito_resultante === 'verde',
  ) ?? sugestoes.find((s) => SUBDIVISAO_POR_ACAO[s.acao] !== undefined);
  const subdivisaoEscape = escape ? SUBDIVISAO_POR_ACAO[escape.acao] : undefined;

  return (
    <Alert variant={VARIANT_POR_VEREDITO[veredito]} className="mt-3 mb-0 py-2 px-3">
      <div className="small d-flex align-items-center flex-wrap gap-1">
        <strong>Complexidade:</strong>
        <span data-testid="windx-medidor-complexidade-series">
          {`${nSeries.toLocaleString('pt-BR')} séries`}
        </span>
        <TooltipWrapper
          title={detalheFatores}
          placement="top"
          className="windx-medidor-complexidade-info"
        >
          <Icon iconType="info" size="small" color="currentColor" />
        </TooltipWrapper>
        {isFetching && <span>…</span>}
      </div>

      {veredito === 'amarelo' && (
        <>
          <div className="small mt-1">Muitas linhas para ler com conforto.</div>
          <div className="small">Agregue por conjunto/subsistema ou use o zoom.</div>
        </>
      )}

      {veredito === 'vermelho' && (
        <div className="mt-2">
          <div className="small mb-1 d-flex align-items-center flex-wrap gap-1">
            <strong>Consulta inviável</strong>
            <TooltipWrapper
              title={TOOLTIP_INVIAVEL}
              placement="top"
              className="windx-medidor-complexidade-info"
            >
              <Icon iconType="info" size="small" color="currentColor" />
            </TooltipWrapper>
          </div>
          <div className="small mb-2">Reduza a seleção ou agregue:</div>
          {escape && subdivisaoEscape !== undefined && (
            <Button type="secondary" onClick={() => onAgregar(subdivisaoEscape)}>
              {LABEL_ACAO[escape.acao]}
              {' '}
              (
              {escape.n_series_resultante.toLocaleString('pt-BR')}
              {' '}
              séries)
            </Button>
          )}
        </div>
      )}
    </Alert>
  );
}
